/**
 * Copilot-only chatbot panel (desktop and mobile body).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box } from '@mui/material';
import { useAISearchPanel } from '@hooks/useAISearchPanel';
import { CopilotChatPanel } from './CopilotChatPanel';
import { CopilotConnectingState } from './CopilotConnectingState';
import { CopilotPanelToolbar } from './CopilotPanelToolbar';
import { useCopilotChatPanel } from './useCopilotChatPanel';
import { copilotConnectingShellSx } from './copilot.styles';

/** Minimum time to show the connecting state on open, even if the session is already cached. */
const MIN_CONNECTING_DISPLAY_MS = 700;

export const CopilotChatbotPanel: React.FC = () => {
  const navigate = useNavigate();
  const { isVisible, hidePanel } = useAISearchPanel();
  const { copilot } = useCopilotChatPanel();
  const [showConnectingOnOpen, setShowConnectingOnOpen] = useState(false);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    setShowConnectingOnOpen(true);
    const timer = window.setTimeout(() => {
      setShowConnectingOnOpen(false);
    }, MIN_CONNECTING_DISPLAY_MS);

    return () => window.clearTimeout(timer);
  }, [isVisible]);

  const handleNewChat = useCallback(async () => {
    await copilot.startNewChat();
  }, [copilot.startNewChat]);

  const handleSuggestAIUseCase = useCallback(() => {
    hidePanel();
    navigate('/suggest-ai-tools');
  }, [hidePanel, navigate]);

  const showUnavailable =
    !copilot.loading && copilot.configured && !copilot.enabled && !copilot.error;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        background: 'linear-gradient(180deg, #FFFFFF 0%, #FAFBFC 100%)',
        borderRadius: 0,
        overflow: 'hidden',
      }}
    >
      <CopilotPanelToolbar
        onNewChat={handleNewChat}
        onSuggestAIUseCase={handleSuggestAIUseCase}
        onHide={hidePanel}
      />

      {showUnavailable ? (
        <Box sx={{ p: 2 }}>
          <Alert severity="info">
            AI Assistant is not available. Please contact your administrator.
          </Alert>
        </Box>
      ) : (
        <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex' }}>
          <CopilotChatPanel
            copilot={copilot}
            onNewConversation={handleNewChat}
            isPanelVisible={isVisible}
          />

          {showConnectingOnOpen && (
            <Box
              sx={{
                ...copilotConnectingShellSx,
                position: 'absolute',
                inset: 0,
                zIndex: 1,
                backgroundColor: 'background.paper',
              }}
            >
              <CopilotConnectingState />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
